import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTexts } from '../config/texts';
import { STORAGE_KEYS } from '../config/constants';
import { loginWithEmail, registerWithEmail, loginWithGoogle, requestPasswordReset, resetPassword, verifyResetToken } from '../utils/api';
import './Login.css';

const Login = ({ language }) => {
  const { setShowLogin, login } = useAuth();
  const [view, setView] = useState('choice'); // choice, login, register, forgot, reset
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    newPassword: '',
    resetToken: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // 检查 URL 参数，看是否是从重置邮件链接跳转过来的
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');

    if (token && email) {
      setFormData(prev => ({
        ...prev,
        email: email,
        resetToken: token
      }));
      setView('reset');
    }
  }, []);

  const t = getTexts('login', language);

  const handleClose = () => {
    setShowLogin(false);
  };

  const handleGoogleLogin = () => {
    // 使用 Google Sign-In SDK
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID',
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          console.error('Google Sign-In popup not displayed');
        }
      });
    } else {
      console.error('Google Sign-In SDK not loaded');
      setError('Google SDK 未加载，请刷新页面重试');
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    setLoading(true);
    setError('');
    try {
      const result = await loginWithGoogle(response.credential);
      if (result.code === 200) {
        const userData = {
          ...result.data.user,
          isNewUser: result.data.is_new_user
        };
        // 传递完整的登录响应，包含user_id和app_specific_id
        login(userData, result.data);
        localStorage.setItem(STORAGE_KEYS.TOKEN, result.data.access_token);
      } else {
        setError(result.message || 'Google 登录失败');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError('Google 登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailContinue = () => {
    setView('login');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (view === 'login') {
        // 邮箱登录
        const result = await loginWithEmail(formData.email, formData.password);
        if (result.code === 200) {
          // 传递完整的登录响应，包含user_id和app_specific_id
          login(result.data.user, result.data);
          localStorage.setItem(STORAGE_KEYS.TOKEN, result.data.access_token);
        } else {
          setError(result.message || t.error);
        }
      } else if (view === 'register') {
        if (formData.password !== formData.confirmPassword) {
          setError(t.passwordMismatch);
          setLoading(false);
          return;
        }
        // 邮箱注册
        const result = await registerWithEmail(formData.email, formData.password, formData.name);
        if (result.code === 200 || result.code === 201) {
          setView('login');
          alert(t.registrationSuccess);
        } else {
          setError(result.message || t.error);
        }
      } else if (view === 'forgot') {
        // 请求密码重置邮件
        if (!formData.email) {
          setError('请输入邮箱地址');
          return;
        }

        const result = await requestPasswordReset(formData.email);
        if (result.code === 200) {
          alert('密码重置邮件已发送，请查收邮箱（可能需要在垃圾邮件中查找）');
          setView('login');
        } else {
          setError(result.message || t.error);
        }
      } else if (view === 'reset') {
        // 重置密码
        if (!formData.newPassword) {
          setError('请输入新密码');
          return;
        }

        if (formData.newPassword.length < 6) {
          setError('密码长度至少为 6 个字符');
          return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
          setError('两次输入的密码不一致');
          return;
        }

        const result = await resetPassword(
          formData.email,
          formData.resetToken,
          formData.newPassword
        );

        if (result.code === 200) {
          setResetSuccess(true);
          setTimeout(() => {
            setView('login');
            setResetSuccess(false);
          }, 3000);
        } else {
          setError(result.message || '密码重置失败');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(t.error || '网络请求失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay" onClick={handleClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-close-btn" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>

        <div className="login-content">
          {view === 'choice' && (
            <>
              <div className="login-header">
                <h2>{t.title}</h2>
                <p>{t.subtitle}</p>
              </div>

              <div className="login-options">
                <button className="google-btn" onClick={handleGoogleLogin}>
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path d="M17.64 9.2c0-.637-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.715H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.272C4.672 5.142 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  {t.googleSignIn}
                </button>

                <button className="email-btn" onClick={handleEmailContinue}>
                  <i className="fas fa-envelope"></i>
                  {t.emailContinue}
                </button>
              </div>
            </>
          )}

          {(view === 'login' || view === 'register' || view === 'forgot' || view === 'reset') && (
            <>
              <button className="back-btn" onClick={() => setView('choice')}>
                <i className="fas fa-arrow-left"></i>
                {t.back}
              </button>

              <div className="login-header">
                <h2>
                  {view === 'login' && t.login}
                  {view === 'register' && t.register}
                  {view === 'forgot' && t.forgotPassword}
                  {view === 'reset' && '重置密码'}
                </h2>
              </div>

              <form className="login-form" onSubmit={handleSubmit}>
                {view === 'register' && (
                  <div className="form-group">
                    <label>{t.name}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder={t.name}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{t.email}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t.email}
                    disabled={view === 'reset'}
                  />
                </div>

                {view !== 'forgot' && view !== 'reset' && (
                  <div className="form-group">
                    <label>{t.password}</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      placeholder={t.password}
                      minLength="6"
                    />
                  </div>
                )}

                {view === 'register' && (
                  <div className="form-group">
                    <label>{t.confirmPassword}</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      placeholder={t.confirmPassword}
                      minLength="6"
                    />
                  </div>
                )}

                {view === 'reset' && (
                  <>
                    <div className="form-group">
                      <label>新密码</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        required
                        placeholder="请输入新密码（至少6个字符）"
                        minLength="6"
                      />
                    </div>
                    <div className="form-group">
                      <label>确认新密码</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        placeholder="请再次输入新密码"
                        minLength="6"
                      />
                    </div>
                  </>
                )}

                {error && <div className="login-error">{error}</div>}
                {resetSuccess && (
                  <div className="login-success">
                    <i className="fas fa-check-circle"></i>
                    密码重置成功！3秒后跳转到登录页...
                  </div>
                )}

                <button type="submit" className="submit-btn" disabled={loading || resetSuccess}>
                  {loading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : view === 'login' ? (
                    t.submit
                  ) : view === 'register' ? (
                    t.registerSubmit
                  ) : view === 'forgot' ? (
                    '发送重置邮件'
                  ) : (
                    '重置密码'
                  )}
                </button>
              </form>

              <div className="login-footer">
                {view === 'login' && (
                  <>
                    <button className="link-btn" onClick={() => setView('forgot')}>
                      {t.forgotPassword}
                    </button>
                    <p>
                      {t.noAccount}{' '}
                      <button className="link-btn" onClick={() => setView('register')}>
                        {t.register}
                      </button>
                    </p>
                  </>
                )}
                {view === 'register' && (
                  <p>
                    {t.hasAccount}{' '}
                    <button className="link-btn" onClick={() => setView('login')}>
                      {t.login}
                    </button>
                  </p>
                )}
                {view === 'forgot' && (
                  <p>
                    <button className="link-btn" onClick={() => setView('login')}>
                      {t.back} {t.login}
                    </button>
                  </p>
                )}
                {view === 'reset' && (
                  <p>
                    <button className="link-btn" onClick={() => setView('login')}>
                      返回登录
                    </button>
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
